import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MENU, canSee, ROLE_LABELS } from "@/lib/menu";
import { useAuth } from "@/context/AuthContext";
import { Leaf, LogOut, Menu as MenuIcon, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const current = MENU.find((m) => m.path && location.pathname.startsWith(m.path));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <Link to="/app/dashboard" className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200 shrink-0">
        <div className="h-9 w-9 rounded-lg bg-green-700 flex items-center justify-center">
          <Leaf className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-slate-900 text-sm">SIA-HPP</div>
          <div className="text-[10px] tracking-wider text-green-700 font-semibold">GREENCON</div>
        </div>
      </Link>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {MENU.map((item, i) => {
          if (item.section) {
            const visible = MENU.filter((m) => m.key && canSee(m, user?.role));
            // hide section label if no children of that section visible is complex; show always for simplicity
            return (
              <div key={`sec-${i}`} className="px-3 pt-4 pb-1 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">
                {item.section}
              </div>
            );
          }
          if (!canSee(item, user?.role)) return null;
          const active = location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <Link
              key={item.key}
              to={item.path}
              data-testid={`sidebar-menu-${item.key}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                active ? "bg-green-50 text-green-800 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <Icon className={`h-[18px] w-[18px] ${active ? "text-green-700" : "text-slate-400"}`} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-slate-200 shrink-0">
        <p className="text-[11px] font-semibold text-slate-700 leading-tight">Politeknik Negeri Manado</p>
        <p className="text-[10px] text-slate-400 mt-0.5">Green Costing Information System</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 bg-white border-r border-slate-200 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-slate-200">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400" data-testid="close-sidebar-btn">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="open-sidebar-btn">
              <MenuIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="font-heading font-semibold text-slate-900 text-base lg:text-lg">{current?.label || "Dashboard"}</h1>
              <p className="text-xs text-slate-400 hidden sm:block">SIA-HPP GREENCON · Green Costing Information System</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 hover:bg-slate-50 transition-colors" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-green-700 text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-slate-900 leading-none">{user?.name}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{ROLE_LABELS[user?.role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-slate-400 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">{children}</main>
      </div>
    </div>
  );
}
